import { Button, Group } from '@mantine/core';
import { IconFileUpload, IconSparkles } from '@tabler/icons-react';
import { Link } from 'react-router';
import { useAskSiesta } from 'src/cashflows/modelling/hooks/useAskSiesta';

/** Whose documents the buttons offer to process — one button each, named where there are several. */
export type DataSubject = { clientId: string; firstName?: string };

type Props = {
  subjects: DataSubject[];
  /** What Siesta is asked for. A household tab must ask about the household, not its first member. */
  siestaPrompt: string;
  siestaLabel?: string;
  compact?: boolean;
};

/**
 * The two ways to fill a thin profile: process the documents, or ask Siesta to read them. Both
 * callouts that offer them render this, so they cannot drift apart — they already named the
 * client differently.
 *
 * Siesta is anchored to the first subject because a conversation belongs to one client, which is
 * why a household caller has to put the household into the prompt itself.
 */
export function DataSourceActions({ subjects, siestaPrompt, siestaLabel, compact }: Props) {
  const askSiesta = useAskSiesta();
  const nameButtons = subjects.length > 1;
  const size = compact ? 'compact-xs' : 'xs';

  return (
    <Group gap="xs" mt="sm">
      {subjects.map(subject => (
        <Button
          key={subject.clientId}
          size={size}
          variant="light"
          component={Link}
          to={`/client/${subject.clientId}/document/add`}
          leftSection={<IconFileUpload size={14} />}
        >
          {nameButtons && subject.firstName
            ? `Process ${subject.firstName}'s documents`
            : 'Process documents'}
        </Button>
      ))}
      <Button
        size={size}
        variant="light"
        leftSection={<IconSparkles size={14} />}
        onClick={() => void askSiesta(siestaPrompt, subjects[0]?.clientId)}
      >
        {siestaLabel ?? 'Ask Siesta'}
      </Button>
    </Group>
  );
}
